/**
 * RoadmapRail — immersive vertical phase rail.
 *
 * Rail is a subtle luminous line. Nodes sit on the rail; completed nodes
 * glow softly, active pulses gently, locked nodes recede. Restrained.
 */
import { useState } from "react";
import { ChevronRight, ChevronDown } from "lucide-react";
import { StatusPill } from "./StatusPill";
import MilestoneRow from "./MilestoneRow";
import { phaseStatus, weightedProgress, PHASE_TITLES } from "./progressCalc";

function nodeRingCls(status) {
    switch (status) {
        case "COMPLETE":
        case "COMPLETE_WITH_EXCEPTION":
            return "bg-emerald-400/80 shadow-[0_0_14px_rgba(52,211,153,0.55)]";
        case "IN_PROGRESS":
            return "bg-qoryn-cyan animate-pulse shadow-[0_0_14px_rgba(0,229,255,0.55)]";
        case "FOUNDER_REVIEW":
            return "bg-amber-300 shadow-[0_0_10px_rgba(252,211,77,0.4)]";
        case "WAITING_EXTERNAL":
            return "bg-orange-300";
        case "BLOCKED":
            return "bg-red-400 shadow-[0_0_12px_rgba(248,113,113,0.4)]";
        case "PLANNED":
            return "bg-purple-300/50";
        case "LOCKED":
            return "bg-white/15";
        case "DEFERRED":
            return "bg-white/20";
        default:
            return "bg-white/25";
    }
}

function PhaseCard({ code, milestones, expanded, onToggle, isFounder, onEdit, dim }) {
    const status = phaseStatus(milestones);
    const p = weightedProgress(milestones);
    const pct = Math.round(p * 100);
    const completed = milestones.filter((m) => ["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(m.status)).length;
    const remaining = milestones.length - completed;
    const launchCriticalCount = milestones.filter((m) => m.launch_critical && !["COMPLETE", "COMPLETE_WITH_EXCEPTION"].includes(m.status)).length;
    return (
        <div
            data-testid={`gtm-phase-${code}`}
            data-status={status}
            className={`relative pl-10 pr-1 ${dim ? "opacity-60" : ""}`}
        >
            {/* Rail node */}
            <span
                aria-hidden
                className={`absolute left-[13px] top-4 h-3 w-3 rounded-full ring-2 ring-qoryn-bg ${nodeRingCls(status)}`}
                data-testid={`gtm-phase-node-${code}`}
            />
            {/* Card */}
            <div className="qoryn-panel rounded-2xl overflow-hidden">
                <button
                    type="button"
                    data-testid={`gtm-phase-toggle-${code}`}
                    onClick={onToggle}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors qoryn-focus"
                >
                    {expanded
                        ? <ChevronDown className="h-4 w-4 text-qoryn-textDim shrink-0" strokeWidth={1.75} />
                        : <ChevronRight className="h-4 w-4 text-qoryn-textDim shrink-0" strokeWidth={1.75} />}
                    <div className="flex-1 min-w-0">
                        <div className="font-display text-[15px] text-qoryn-text tracking-tight truncate">
                            {PHASE_TITLES[code] ?? code}
                        </div>
                        <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim mt-0.5">
                            {completed} completed · {remaining} remaining{launchCriticalCount > 0 ? ` · ${launchCriticalCount} launch-critical` : ""}
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-2 shrink-0">
                        <div className="w-20 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                            <div className="h-full bg-qoryn-cyan/60 transition-[width] duration-500 ease-out" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textDim w-8 text-right">
                            {pct}%
                        </span>
                    </div>
                    <StatusPill status={status} />
                </button>
                {expanded && (
                    <div data-testid={`gtm-phase-milestones-${code}`} className="px-3 pb-3 pt-1 space-y-1.5 border-t border-qoryn-border/40">
                        {milestones.map((m) => (
                            <MilestoneRow key={m.code} milestone={m} isFounder={isFounder} onEdit={onEdit} />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function RoadmapRail({ phasesGrouped, isFounder, onEdit, initiallyExpandedPhases = [] }) {
    const [expanded, setExpanded] = useState(() => new Set(initiallyExpandedPhases));
    const toggle = (code) => {
        setExpanded((prev) => {
            const next = new Set(prev);
            if (next.has(code)) next.delete(code); else next.add(code);
            return next;
        });
    };
    return (
        <div data-testid="gtm-roadmap-rail" className="relative">
            {/* Vertical rail line */}
            <span
                aria-hidden
                className="absolute left-[18px] top-3 bottom-3 w-px bg-gradient-to-b from-qoryn-cyan/30 via-white/10 to-white/5"
            />
            <div className="space-y-3">
                {phasesGrouped.map(([code, milestones]) => (
                    <PhaseCard
                        key={code}
                        code={code}
                        milestones={milestones}
                        expanded={expanded.has(code)}
                        onToggle={() => toggle(code)}
                        isFounder={isFounder}
                        onEdit={onEdit}
                        dim={milestones.every((m) => ["LOCKED", "DEFERRED"].includes(m.status))}
                    />
                ))}
            </div>
        </div>
    );
}
